import React from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '../contexts/LanguageContext';

const SEO = () => {
  const { language } = useLanguage();
  
  // All domains for the organization
  const domains = {
    primary: 'https://ngo.phnx.dev',
    alternative: 'https://your-ngo.org',
    wwwPrimary: 'https://www.ngo.phnx.dev',
    wwwAlternative: 'https://www.your-ngo.org',
    projects: 'https://ngoproject.phnx.dev'
  };
  
  // Determine current domain
  const getCurrentDomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      const currentUrl = `${protocol}//${hostname}`;
      
      // Check which domain matches
      for (const [key, domain] of Object.entries(domains)) {
        if (currentUrl === domain) {
          return { currentDomain: domain, isPrimary: key === 'primary' };
        }
      }
      
      // Default to primary domain
      return { currentDomain: domains.primary, isPrimary: true };
    }
    return { currentDomain: domains.primary, isPrimary: true };
  };
  
  const { currentDomain, isPrimary } = getCurrentDomain();
  
  // Base URL for canonical and meta tags (use primary domain for consistency)
  const baseUrl = isPrimary ? currentDomain : domains.primary;
  
  // Language-specific URLs for the current domain
  const languageUrls = {
    en: `${currentDomain}/en`,
    fr: `${currentDomain}/fr`,
    es: `${currentDomain}/es`,
    ca: `${currentDomain}/ca`,
    de: `${currentDomain}/de`
  };
  
  // Language-specific URLs for primary domain (for hreflang consistency)
  const primaryLanguageUrls = {
    en: `${domains.primary}/en`,
    fr: `${domains.primary}/fr`,
    es: `${domains.primary}/es`,
    ca: `${domains.primary}/ca`,
    de: `${domains.primary}/de`
  };
  
  // Language-specific keywords for SEO optimization
  const languageKeywords = {
    en: 'non-profit, humanitarian, community development, sustainability, peace, unity, love, harmony, consciousness, donate to non-profit, support humanitarian work, charity donations online, donate to NGO Europe, monthly donation charity, sponsor a child NGO, humanitarian aid donations, non-profit organization donations, emergency relief donations, sustainable development donations, community development funding, education project donations, health initiative funding, environmental charity donations, peace building donations, building bridges not walls, humanitarian organization values, community development NGO, sustainable development goals, peace and unity organization, love and harmony NGO, consciousness raising charity, human rights organization, social justice charity, empowerment through education, Haiti humanitarian projects, Mexico community development, Latin America NGO work, Caribbean sustainable development, Dominican Republic aid, Western Sahara humanitarian, Colombia peace initiatives, Peru education programs, Ecuador health projects, Chile environmental charity, humanitarian aid programs, community development projects, sustainable agriculture NGO, education for underprivileged, healthcare access charity, environmental conservation NGO, women empowerment programs, youth development initiatives, disaster relief organization, clean water projects, YourNGO NGO, YourNGO organization, your-ngo.org, ngo.phnx.dev donations, YourNGO reviews',
    fr: 'organisation à but non lucratif, humanitaire, développement communautaire, durabilité, paix, unité, amour, harmonie, conscience, faire un don à une organisation à but non lucratif, soutenir le travail humanitaire, dons de charité en ligne, faire un don à une ONG Europe, don mensuel à une œuvre de charité, parrainer un enfant ONG, dons d\'aide humanitaire, dons à des organisations à but non lucratif, dons de secours d\'urgence, dons de développement durable, financement du développement communautaire, dons de projets éducatifs, financement d\'initiatives de santé, dons de charité environnementale, dons de construction de la paix, construire des ponts pas des murs, valeurs des organisations humanitaires, ONG de développement communautaire, objectifs de développement durable, organisation de paix et d\'unité, ONG d\'amour et d\'harmonie, charité de sensibilisation, organisation des droits de l\'homme, charité de justice sociale, autonomisation par l\'éducation, projets humanitaires Haïti, développement communautaire Mexique, travail ONG Amérique latine, développement durable Caraïbes, aide République dominicaine, humanitaire Sahara occidental, initiatives de paix Colombie, programmes éducatifs Pérou, projets de santé Équateur, charité environnementale Chili, programmes d\'aide humanitaire, projets de développement communautaire, ONG d\'agriculture durable, éducation pour les défavorisés, charité d\'accès aux soins de santé, ONG de conservation environnementale, programmes d\'autonomisation des femmes, initiatives de développement des jeunes, organisation de secours en cas de catastrophe, projets d\'eau potable, ONG YourNGO, organisation YourNGO, your-ngo.org, dons ngo.phnx.dev, avis YourNGO',
    es: 'organización sin fines de lucro, humanitaria, desarrollo comunitario, sostenibilidad, paz, unidad, amor, armonía, conciencia, donar a organización sin fines de lucro, apoyar trabajo humanitario, donaciones de caridad en línea, donar a ONG Europa, donación mensual a caridad, apadrinar un niño ONG, donaciones de ayuda humanitaria, donaciones a organizaciones sin fines de lucro, donaciones de ayuda de emergencia, donaciones de desarrollo sostenible, financiación del desarrollo comunitario, donaciones de proyectos educativos, financiación de iniciativas de salud, donaciones de caridad ambiental, donaciones de construcción de paz, construir puentes no muros, valores de organizaciones humanitarias, ONG de desarrollo comunitario, objetivos de desarrollo sostenible, organización de paz y unidad, ONG de amor y armonía, caridad de concienciación, organización de derechos humanos, caridad de justicia social, empoderamiento a través de la educación, proyectos humanitarios Haití, desarrollo comunitario México, trabajo ONG América Latina, desarrollo sostenible Caribe, ayuda República Dominicana, humanitario Sahara Occidental, iniciativas de paz Colombia, programas educativos Perú, proyectos de salud Ecuador, caridad ambiental Chile, programas de ayuda humanitaria, proyectos de desarrollo comunitario, ONG de agricultura sostenible, educación para desfavorecidos, caridad de acceso a la salud, ONG de conservación ambiental, programas de empoderamiento de mujeres, iniciativas de desarrollo juvenil, organización de ayuda en desastres, proyectos de agua potable, ONG YourNGO, organización YourNGO, your-ngo.org, donaciones ngo.phnx.dev, reseñas YourNGO',
    ca: 'organització sense ànim de lucre, humanitària, desenvolupament comunitari, sostenibilitat, pau, unitat, amor, harmonia, consciència, donar a organització sense ànim de lucre, donar suport a la feina humanitària, donacions de caritat en línia, donar a ONG Europa, donació mensual a caritat, apadrinar un nen ONG, donacions d\'ajuda humanitària, donacions a organitzacions sense ànim de lucre, donacions d\'ajuda d\'emergència, donacions de desenvolupament sostenible, finançament del desenvolupament comunitari, donacions de projectes educatius, finançament d\'iniciatives de salut, donacions de caritat ambiental, donacions de construcció de pau, construir ponts no murs, valors d\'organitzacions humanitàries, ONG de desenvolupament comunitari, objectius de desenvolupament sostenible, organització de pau i unitat, ONG d\'amor i harmonia, caritat de conscienciació, organització de drets humans, caritat de justícia social, empoderament a través de l\'educació, projectes humanitaris Haití, desenvolupament comunitari Mèxic, treball ONG Amèrica Llatina, desenvolupament sostenible Carib, ajuda República Dominicana, humanitari Sàhara Occidental, iniciatives de pau Colòmbia, programes educatius Perú, projectes de salut Equador, caritat ambiental Xile, programes d\'ajuda humanitària, projectes de desenvolupament comunitari, ONG d\'agricultura sostenible, educació per a desfavorits, caritat d\'accés a la salut, ONG de conservació ambiental, programes d\'empoderament de dones, iniciatives de desenvolupament juvenil, organització d\'ajuda en desastres, projectes d\'aigua potable, ONG YourNGO, organització YourNGO, your-ngo.org, donacions ngo.phnx.dev, ressenyes YourNGO',
    de: 'gemeinnützige Organisation, humanitär, Gemeindeentwicklung, Nachhaltigkeit, Frieden, Einheit, Liebe, Harmonie, Bewusstsein, an gemeinnützige Organisation spenden, humanitäre Arbeit unterstützen, Wohltätigkeitsspenden online, an NGO Europa spenden, monatliche Spende an Wohltätigkeit, ein Kind NGO sponsern, humanitäre Hilfsspenden, Spenden an gemeinnützige Organisationen, Notfallhilfsspenden, nachhaltige Entwicklungsspenden, Gemeindeentwicklungsfinanzierung, Bildungsprojektspenden, Gesundheitsinitiativenfinanzierung, Umweltwohltätigkeitsspenden, Friedensaufbauspenden, Brücken bauen nicht Mauern, humanitäre Organisationswerte, Gemeindeentwicklungs-NGO, nachhaltige Entwicklungsziele, Friedens- und Einheitsorganisation, Liebe- und Harmonie-NGO, Bewusstseinsfördernde Wohltätigkeit, Menschenrechtsorganisation, soziale Gerechtigkeitswohltätigkeit, Empowerment durch Bildung, humanitäre Projekte Haiti, Gemeindeentwicklung Mexiko, NGO-Arbeit Lateinamerika, nachhaltige Entwicklung Karibik, Hilfe Dominikanische Republik, humanitär Westsahara, Friedensinitiativen Kolumbien, Bildungsprogramme Peru, Gesundheitsprojekte Ecuador, Umweltwohltätigkeit Chile, humanitäre Hilfsprogramme, Gemeindeentwicklungsprojekte, nachhaltige Landwirtschafts-NGO, Bildung für Benachteiligte, Gesundheitszugangswohltätigkeit, Umweltschutz-NGO, Frauenempowerment-Programme, Jugendentwicklungsinitiativen, Katastrophenhilfsorganisation, sauberes Wasser Projekte, YourNGO NGO, YourNGO Organisation, your-ngo.org, ngo.phnx.dev Spenden, YourNGO Bewertungen'
  };
  
  // Site metadata
  const siteMetadata = {
    title: 'YourNGO - Building Bridges Where Walls Stand',
    description: 'YourNGO is a non-profit organization dedicated to building bridges where walls stand. We work on humanitarian projects, community development, and sustainable solutions worldwide.',
    keywords: languageKeywords[language] || languageKeywords.en,
    author: 'YourNGO',
    siteName: 'YourNGO',
    image: `${baseUrl}/logo512.png`,
    logo: `${baseUrl}/logo192.png`,
    type: 'website',
  };
  
  // Language-specific metadata with keyword integration
  const languageMetadata = {
    en: {
      title: 'YourNGO - Building Bridges Where Walls Stand | Humanitarian NGO',
      description: 'Support humanitarian projects worldwide. Donate to YourNGO NGO for community development, sustainable solutions, and peace initiatives in Haiti, Mexico, Latin America, and the Caribbean.',
    },
    fr: {
      title: 'YourNGO - Construire des ponts là où se dressent des murs | ONG Humanitaire',
      description: 'Soutenez des projets humanitaires dans le monde entier. Faites un don à l\'ONG YourNGO pour le développement communautaire, les solutions durables et les initiatives de paix en Haïti, au Mexique, en Amérique latine et dans les Caraïbes.',
    },
    es: {
      title: 'YourNGO - Construyendo puentes donde hay muros | ONG Humanitaria',
      description: 'Apoya proyectos humanitarios en todo el mundo. Dona a la ONG YourNGO para desarrollo comunitario, soluciones sostenibles e iniciativas de paz en Haití, México, América Latina y el Caribe.',
    },
    ca: {
      title: 'YourNGO - Construint ponts on hi ha murs | ONG Humanitària',
      description: 'Dona suport a projectes humanitaris a tot el món. Fes una donació a l\'ONG YourNGO per al desenvolupament comunitari, solucions sostenibles i iniciatives de pau a Haití, Mèxic, Amèrica Llatina i el Carib.',
    },
    de: {
      title: 'YourNGO - Brücken bauen, wo Mauern stehen | Humanitäre NGO',
      description: 'Unterstützen Sie humanitäre Projekte weltweit. Spenden Sie an die NGO YourNGO für Gemeindeentwicklung, nachhaltige Lösungen und Friedensinitiativen in Haiti, Mexiko, Lateinamerika und der Karibik.',
    }
  };
  
  const currentMetadata = languageMetadata[language] || languageMetadata.en;
  
  // Canonical URL should always point to primary domain for SEO consistency
  const canonicalUrl = primaryLanguageUrls[language] || domains.primary;
  
  // Current page URL for Open Graph
  const currentPageUrl = languageUrls[language] || currentDomain;
  
  // JSON-LD structured data for Organization with enhanced context
  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'NGO',
    'name': 'YourNGO',
    'alternateName': 'YourNGO',
    'url': domains.primary,
    'logo': `${domains.primary}/logo512.png`,
    'description': currentMetadata.description,
    'foundingDate': '2020',
    'founder': {
      '@type': 'Person',
      'name': 'YourNGO Team'
    },
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': 'Lorem ipsum Str. 99',
      'addressLocality': 'Lorem City',
      'postalCode': '03700',
      'addressCountry': 'ES',
      'addressRegion': 'Alicante'
    },
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'customer service',
      'email': 'contact@your-ngo.org',
      'telephone': '+34-123-456-789',
      'availableLanguage': ['English', 'Spanish', 'French', 'Catalan', 'German']
    },
    'sameAs': [
      'https://www.your-ngo.org/',
      'https://www.facebook.com/yourngo/'
    ],
    'taxID': 'G12345678', // Example tax ID for Spain
    'nonprofitStatus': 'Nonprofit501c3',
    'keywords': languageKeywords[language] || languageKeywords.en,
    'knowsAbout': ['Human Rights', 'Community Development', 'Sustainability', 'Peace Building', 'Documentary Filmmaking'],
    'memberOf': [
      {
        '@type': 'Organization',
        'name': 'United Nations NGOs'
      }
    ],
    'areaServed': [
      {
        '@type': 'Place',
        'name': 'Haiti',
        'description': 'Humanitarian projects and community development'
      },
      {
        '@type': 'Place',
        'name': 'Mexico',
        'description': 'Community development and sustainable solutions'
      },

      {'@type': 'Place',
      'name': 'Lorem City',
      'description': 'Community development and sustainable solutions'
      },
      {
        '@type': 'Place',
        'name': 'Latin America',
        'description': 'Regional humanitarian initiatives'
      },
      {
        '@type': 'Place',
        'name': 'Caribbean',
        'description': 'Sustainable development and aid programs'
      }
    ],
    'service': [
      {
        '@type': 'Service',
        'name': 'Humanitarian Aid',
        'description': 'Emergency relief and humanitarian assistance'
      },
      {
        '@type': 'Service',
        'name': 'Community Development',
        'description': 'Sustainable community development projects'
      },
      {
        '@type': 'Service',
        'name': 'Education Programs',
        'description': 'Educational initiatives for underprivileged communities'
      },
      {
        '@type': 'Service',
        'name': 'Peace Building',
        'description': 'Peace initiatives and conflict resolution'
      }
    ]
  };
  
  // JSON-LD structured data for Website
  const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'YourNGO',
    'url': domains.primary,
    'potentialAction': {
      '@type': 'SearchAction',
      'target': `${domains.primary}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };
  
  // WebPage structured data for current page
  const webpageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': currentMetadata.title,
    'description': currentMetadata.description,
    'url': currentPageUrl,
    'inLanguage': language,
    'isPartOf': {
      '@type': 'WebSite',
      'name': 'YourNGO',
      'url': domains.primary
    },
    'datePublished': '2020-01-01',
    'dateModified': new Date().toISOString().split('T')[0],
    'potentialAction': {
      '@type': 'ReadAction',
      'target': [currentPageUrl]
    }
  };
  
  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': domains.primary
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': language === 'en' ? 'English' : 
                language === 'fr' ? 'Français' :
                language === 'es' ? 'Español' :
                language === 'ca' ? 'Català' :
                language === 'de' ? 'Deutsch' : 'English',
        'item': currentPageUrl
      }
    ]
  };

  // Multilingual FAQ structured data for AI search optimization with donation focus
  const faqData = {
    en: [
      {
        '@type': 'Question',
        'name': 'What is YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'YourNGO is a non-profit organization dedicated to building bridges where walls stand. We work on humanitarian projects, community development, and sustainable solutions worldwide.'
        }
      },
      {
        '@type': 'Question',
        'name': 'What does YourNGO do?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'We work on various humanitarian projects including community development, sustainable solutions, peace initiatives, and educational programs across different countries like Haiti, Mexico, Latin America, and the Caribbean.'
        }
      },
      {
        '@type': 'Question',
        'name': 'How can I support YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'You can support us through donations, volunteering, spreading awareness about our mission, or partnering with us on projects. Monthly subscriptions and one-time donations help fund our humanitarian work.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Is my donation to YourNGO tax-deductible?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Yes, YourNGO is a registered non-profit organization, and donations are tax-deductible in accordance with local regulations. You will receive a donation receipt for tax purposes.'
        }
      },
      {
        '@type': 'Question',
        'name': 'How can I donate to YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'You can donate through our secure website using PayPal. We offer monthly subscription plans (Seed Planter €10/month, Bridge Builder €25/month, Anchor Point €50/month) and one-time donation options.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Where does my donation to YourNGO go?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Your donation directly funds humanitarian projects, community development initiatives, sustainable solutions, and peace-building programs in regions like Haiti, Mexico, Latin America, and the Caribbean.'
        }
      }
    ],
    fr: [
      {
        '@type': 'Question',
        'name': 'Qu\'est-ce que YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'YourNGO est une organisation à but non lucratif dédiée à construire des ponts là où se dressent des murs. Nous travaillons sur des projets humanitaires, le développement communautaire et des solutions durables dans le monde entier.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Que fait YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Nous travaillons sur divers projets humanitaires incluant le développement communautaire, des solutions durables, des initiatives de paix et des programmes éducatifs dans différents pays comme Haïti, le Mexique, l\'Amérique latine et les Caraïbes.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Comment puis-je soutenir YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Vous pouvez nous soutenir par des dons, du bénévolat, en faisant connaître notre mission ou en collaborant avec nous sur des projets. Les abonnements mensuels et les dons ponctuels aident à financer notre travail humanitaire.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Mon don à YourNGO est-il déductible des impôts?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Oui, YourNGO est une organisation à but non lucratif enregistrée, et les dons sont déductibles des impôts conformément aux réglementations locales. Vous recevrez un reçu de don à des fins fiscales.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Comment puis-je faire un don à YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Vous pouvez faire un don via notre site web sécurisé en utilisant PayPal. Nous proposons des plans d\'abonnement mensuel (Planteur de graines 10€/mois, Constructeur de ponts 25€/mois, Point d\'ancrage 50€/mois) et des options de don ponctuel.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Où va mon don à YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Votre don finance directement des projets humanitaires, des initiatives de développement communautaire, des solutions durables et des programmes de consolidation de la paix dans des régions comme Haïti, le Mexique, l\'Amérique latine et les Caraïbes.'
        }
      }
    ],
    es: [
      {
        '@type': 'Question',
        'name': '¿Qué es YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'YourNGO es una organización sin fines de lucro dedicada a construir puentes donde hay muros. Trabajamos en proyectos humanitarios, desarrollo comunitario y soluciones sostenibles en todo el mundo.'
        }
      },
      {
        '@type': 'Question',
        'name': '¿Qué hace YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Trabajamos en varios proyectos humanitarios que incluyen desarrollo comunitario, soluciones sostenibles, iniciativas de paz y programas educativos en diferentes países como Haití, México, América Latina y el Caribe.'
        }
      },
      {
        '@type': 'Question',
        'name': '¿Cómo puedo apoyar a YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Puedes apoyarnos a través de donaciones, voluntariado, difundiendo nuestra misión o colaborando con nosotros en proyectos. Las suscripciones mensuales y las donaciones únicas ayudan a financiar nuestro trabajo humanitario.'
        }
      },
      {
        '@type': 'Question',
        'name': '¿Es mi donación a YourNGO deducible de impuestos?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Sí, YourNGO es una organización sin fines de lucro registrada, y las donaciones son deducibles de impuestos de acuerdo con las regulaciones locales. Recibirás un recibo de donación para fines fiscales.'
        }
      },
      {
        '@type': 'Question',
        'name': '¿Cómo puedo donar a YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Puedes donar a través de nuestro sitio web seguro usando PayPal. Ofrecemos planes de suscripción mensual (Sembrador de Semillas 10€/mes, Constructor de Puentes 25€/mes, Punto de Anclaje 50€/mes) y opciones de donación única.'
        }
      },
      {
        '@type': 'Question',
        'name': '¿A dónde va mi donación a YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Tu donación financia directamente proyectos humanitarios, iniciativas de desarrollo comunitario, soluciones sostenibles y programas de construcción de paz en regiones como Haití, México, América Latina y el Caribe.'
        }
      }
    ],
    ca: [
      {
        '@type': 'Question',
        'name': 'Què és YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'YourNGO és una organització sense ànim de lucre dedicada a construir ponts on hi ha murs. Treballem en projectes humanitaris, desenvolupament comunitari i solucions sostenibles a tot el món.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Què fa YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Treballem en diversos projectes humanitaris que inclouen desenvolupament comunitari, solucions sostenibles, iniciatives de pau i programes educatius en diferents països com Haití, Mèxic, Amèrica Llatina i el Carib.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Com puc donar suport a YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Pots donar-nos suport a través de donacions, voluntariat, difonent la nostra missió o col·laborant amb nosaltres en projectes. Les subscripcions mensuals i les donacions úniques ajuden a finançar la nostra feina humanitària.'
        }
      },
      {
        '@type': 'Question',
        'name': 'És la meva donació a YourNGO deduïble d\'impostos?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Sí, YourNGO és una organització sense ànim de lucre registrada, i les donacions són deduïbles d\'impostos d\'acord amb les regulacions locals. Rebràs un rebut de donació per a finalitats fiscals.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Com puc fer una donació a YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Pots fer una donació a través del nostre lloc web segur utilitzant PayPal. Oferim plans de subscripció mensual (Sembrador de Llavors 10€/mes, Constructor de Ponts 25€/mes, Punt d\'Ancoratge 50€/mes) i opcions de donació única.'
        }
      },
      {
        '@type': 'Question',
        'name': 'On va la meva donació a YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'La teva donació finança directament projectes humanitaris, iniciatives de desenvolupament comunitari, solucions sostenibles i programes de construcció de pau en regions com Haití, Mèxic, Amèrica Llatina i el Carib.'
        }
      }
    ],
    de: [
      {
        '@type': 'Question',
        'name': 'Was ist YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'YourNGO ist eine gemeinnützige Organisation, die sich dem Bau von Brücken dort widmet, wo Mauern stehen. Wir arbeiten an humanitären Projekten, Gemeindeentwicklung und nachhaltigen Lösungen weltweit.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Was macht YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Wir arbeiten an verschiedenen humanitären Projekten einschließlich Gemeindeentwicklung, nachhaltigen Lösungen, Friedensinitiativen und Bildungsprogrammen in verschiedenen Ländern wie Haiti, Mexiko, Lateinamerika und der Karibik.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Wie kann ich YourNGO unterstützen?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Sie können uns durch Spenden, Freiwilligenarbeit, Verbreitung unserer Mission oder Partnerschaften bei Projekten unterstützen. Monatliche Abonnements und Einmalspenden helfen, unsere humanitäre Arbeit zu finanzieren.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Ist meine Spende an YourNGO steuerlich absetzbar?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Ja, YourNGO ist eine registrierte gemeinnützige Organisation, und Spenden sind gemäß lokaler Vorschriften steuerlich absetzbar. Sie erhalten eine Spendenquittung für Steuerzwecke.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Wie kann ich an YourNGO spenden?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Sie können über unsere sichere Website mit PayPal spenden. Wir bieten monatliche Abonnementpläne (Samenpflanzer 10€/Monat, Brückenbauer 25€/Monat, Ankerpunkt 50€/Monat) und Einmalspendenoptionen an.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Wohin geht meine Spende an YourNGO?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Ihre Spende finanziert direkt humanitäre Projekte, Gemeindeentwicklungsinitiativen, nachhaltige Lösungen und Friedensaufbauprogramme in Regionen wie Haiti, Mexiko, Lateinamerika und der Karibik.'
        }
      }
    ]
  };
  
  // Get FAQ data for current language
  const currentFaqData = faqData[language] || faqData.en;
  
  // FAQ structured data for AI search optimization with donation focus
  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': currentFaqData
  };
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{currentMetadata.title}</title>
      <meta name="description" content={currentMetadata.description} />
      <meta name="keywords" content={siteMetadata.keywords} />
      <meta name="author" content={siteMetadata.author} />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Performance and Security Meta Tags - 2025 Best Practices */}
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="format-detection" content="telephone=no, email=no, address=no" />
      {/* Note: Content-Security-Policy should be set at server level for better security */}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Alternate language URLs for hreflang - point to primary domain */}
      <link rel="alternate" hreflang="en" href={primaryLanguageUrls.en} />
      <link rel="alternate" hreflang="fr" href={primaryLanguageUrls.fr} />
      <link rel="alternate" hreflang="es" href={primaryLanguageUrls.es} />
      <link rel="alternate" hreflang="ca" href={primaryLanguageUrls.ca} />
      <link rel="alternate" hreflang="de" href={primaryLanguageUrls.de} />
      <link rel="alternate" hreflang="x-default" href={domains.primary} />
      
      {/* Performance Optimization Links - 2025 Best Practices */}
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://www.facebook.com" />
      <link rel="dns-prefetch" href="https://www.facebook.com" />
      <link rel="preconnect" href="https://www.instagram.com" />
      <link rel="dns-prefetch" href="https://www.instagram.com" />
      {/* Core Web Vitals Optimization */}
      <link rel="preload" href={`${baseUrl}/logo192.png`} as="image" type="image/png" />
      <link rel="preload" href={`${baseUrl}/logo512.png`} as="image" type="image/png" />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={currentMetadata.title} />
      <meta property="og:description" content={currentMetadata.description} />
      <meta property="og:type" content={siteMetadata.type} />
      <meta property="og:url" content={currentPageUrl} />
      <meta property="og:site_name" content={siteMetadata.siteName} />
      <meta property="og:image" content={siteMetadata.image} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:alt" content="YourNGO Logo" />
      <meta property="og:locale" content={language} />
      <meta property="og:updated_time" content={new Date().toISOString()} />
      
      {/* Additional Open Graph locales for multilingual support */}
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="es_ES" />
      <meta property="og:locale:alternate" content="ca_ES" />
      <meta property="og:locale:alternate" content="de_DE" />
      
      {/* Open Graph see also links */}
      <meta property="og:see_also" href={domains.primary + '/about'} />
      <meta property="og:see_also" href={domains.primary + '/projects'} />
      <meta property="og:see_also" href={domains.primary + '/team'} />
      <meta property="og:see_also" href={domains.primary + '/contact'} />
      
      {/* Twitter Card Meta Tags - Removed as organization doesn't have Twitter */}
      
      {/* Additional Meta Tags - 2025 Best Practices */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes, viewport-fit=cover" />
      <meta name="theme-color" content="#007bff" media="(prefers-color-scheme: light)" />
      <meta name="theme-color" content="#000000" media="(prefers-color-scheme: dark)" />
      <meta name="msapplication-TileColor" content="#007bff" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="mobile-web-app-capable" content="yes" />
      {/* AI/ML Search Optimization */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="google" content="notranslate" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      {/* Accessibility for SEO */}
      <meta name="color-scheme" content="light dark" />
      
      {/* Favicon Links */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.json" />
      
      {/* Structured Data (JSON-LD) - 2025 Best Practices */}
      <script type="application/ld+json">
        {JSON.stringify(organizationStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webpageStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqStructuredData)}
      </script>
      
      {/* Additional SEO Links */}
      <link rel="sitemap" type="application/xml" href="/sitemap.xml" />
      <link rel="alternate" type="application/rss+xml" href="/rss.xml" />
      
      {/* 
        Webmaster Tools Verification Notes:
        
        Google Search Console: Using DNS verification via Cloudflare.
        To verify, add TXT record in Cloudflare DNS with code from Google Search Console.
        
        Bing Webmaster Tools: Can use DNS or HTML tag verification.
        If using HTML tag, add: <meta name="msvalidate.01" content="YOUR_CODE_HERE" />
        
        Yandex Webmaster: Can use DNS or HTML tag verification.
        If using HTML tag, add: <meta name="yandex-verification" content="YOUR_CODE_HERE" />
        
        Pinterest: Requires HTML tag verification.
        Add: <meta name="p:domain_verify" content="YOUR_CODE_HERE" />
        
        Note: Remove these comments after adding actual verification codes.
      */}
      
      {/* Performance Monitoring - Core Web Vitals */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Google Ads Preconnect for better performance */}
      <link rel="preconnect" href="https://www.googleadservices.com" />
      <link rel="preconnect" href="https://googleads.g.doubleclick.net" />
      <link rel="preconnect" href="https://tpc.googlesyndication.com" />
      
      {/* Additional performance optimizations */}
      <link rel="preload" href={`${baseUrl}/static/media/logo_new.ac0467b11d3fd1c7295e.png`} as="image" type="image/png" />
      <link rel="preload" href={`${baseUrl}/static/media/foreground.f670214d23dabaf40526.png`} as="image" type="image/png" />
      
      {/* 
        Google AdSense (if applicable):
        <meta name="google-adsense-account" content="ca-pub-XXXXXXXXXXXXXXXX" />
        
        Note: Uncomment and add your AdSense publisher ID if using Google AdSense.
      */}
    </Helmet>
  );
};

export default SEO;
